import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SellerPageContainer } from '@/components/seller/layout/SellerPageContainer';
import { sellerService } from '@/services/seller';
import { useToast } from '@/components/common/Toast';
import type { OrderWithItems, SalesMetrics } from '@/store/seller';
import { getEffectiveOrderTime } from '@/utils/orderTime';

export function SalesHistoryPage() {
  const navigate = useNavigate();
  const { ToastContainer, showToast } = useToast();

  const [salesMetrics, setSalesMetrics] = useState<SalesMetrics | null>(null);
  const [allOrders, setAllOrders] = useState<OrderWithItems[]>([]);
  const [selectedMonth, setSelectedMonth] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 월 선택 옵션 생성 (최근 6개월)
  const generateMonthOptions = () => {
    const options = [];
    const now = new Date();
    
    for (let i = 0; i < 6; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const value = `${year}-${month.toString().padStart(2, '0')}`;
      const label = `${year}년 ${month}월`;
      
      options.push({ value, label });
    }
    
    return options;
  };

  const monthOptions = generateMonthOptions();

  useEffect(() => {
    // 기본값을 현재 월로 설정
    if (monthOptions.length > 0) {
      setSelectedMonth(monthOptions[0].value);
    }
  }, []);

  useEffect(() => {
    const loadSalesData = async () => {
      if (!selectedMonth) return;
      
      try {
        setLoading(true);
        setError(null);
        
        // 판매자 레스토랑 ID 가져오기
        const restaurantResult = await sellerService.getSellerRestaurantId();
        if (!restaurantResult.success || !restaurantResult.restaurantId) {
          setError(restaurantResult.error || '레스토랑 정보를 찾을 수 없습니다');
          return;
        }
        

        // 대시보드 데이터 가져오기 (매출 정보)
        const dashboardResult = await sellerService.getDashboardData(restaurantResult.restaurantId);
        if (dashboardResult.success && dashboardResult.data) {
          setSalesMetrics(dashboardResult.data.salesMetrics);
        }

        // 모든 주문 목록 가져오기
        const ordersResult = await sellerService.getAllOrdersForRestaurant(restaurantResult.restaurantId);
        if (ordersResult.success && ordersResult.data) {
          // 선택된 월에 해당하는 주문만 필터링
          const filteredOrders = ordersResult.data.filter(order => {
            const orderDate = new Date(order.created_at);
            const orderMonth = `${orderDate.getFullYear()}-${(orderDate.getMonth() + 1).toString().padStart(2, '0')}`;
            return orderMonth === selectedMonth;
          });
          
          setAllOrders(filteredOrders);
        }
        
      } catch (error) {
        console.error('Sales data load error:', error);
        setError('판매 데이터를 불러오는 중 오류가 발생했습니다');
      } finally {
        setLoading(false);
      }
    };

    loadSalesData();
  }, [selectedMonth]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ko-KR').format(amount);
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getOrderTypeLabel = (type: string) => {
    const labels = {
      'DEADLINE_DISCOUNT': '마감할인',
      'RESERVATION_DISCOUNT': '예약할인',
      'REGULAR_ORDER': '일반주문'
    };
    return labels[type as keyof typeof labels] || type;
  };

  const getOrderTypeBadgeColor = (type: string) => {
    const colors = {
      'DEADLINE_DISCOUNT': 'bg-orange-100 text-orange-600',
      'RESERVATION_DISCOUNT': 'bg-blue-100 text-blue-600',
      'REGULAR_ORDER': 'bg-gray-100 text-gray-600'
    };
    return colors[type as keyof typeof colors] || 'bg-gray-100 text-gray-600';
  };

  const getOrderStatusLabel = (status: string) => {
    const labels = {
      'PENDING': '주문접수',
      'ACCEPTED': '접수완료',
      'READY': '준비완료',
      'COMPLETED': '판매완료',
      'CANCELLED': '취소됨',
      'NOSHOW': '노쇼'
    };
    return labels[status as keyof typeof labels] || status;
  };

  const getOrderStatusBadgeColor = (status: string) => {
    const colors = {
      'PENDING': 'bg-yellow-100 text-yellow-700',
      'ACCEPTED': 'bg-blue-100 text-blue-700',
      'READY': 'bg-purple-100 text-purple-700',
      'COMPLETED': 'bg-green-100 text-green-700',
      'CANCELLED': 'bg-red-100 text-red-700',
      'NOSHOW': 'bg-gray-100 text-gray-700'
    };
    return colors[status as keyof typeof colors] || 'bg-gray-100 text-gray-700';
  };

  const getItemsText = (items: any[]) => {
    if (!items || items.length === 0) {
      return '상품 정보 없음';
    }
    
    const firstItem = items[0];
    const restCount = items.length - 1;
    
    if (restCount > 0) {
      return `${firstItem.product_name} 외 ${restCount}개`;
    }
    
    return firstItem.product_name;
  };

  const handleReviewClick = () => {
    showToast('아직 비활성화된 기능입니다');
  };

  if (loading) {
    return (
      <SellerPageContainer activeNavId="store">
        <div className="min-h-screen bg-gray-100 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
            <p className="text-gray-600">판매 데이터를 불러오는 중...</p>
          </div>
        </div>
      </SellerPageContainer>
    );
  }

  if (error) {
    return (
      <SellerPageContainer activeNavId="store">
        <div className="min-h-screen bg-gray-100 flex items-center justify-center">
          <div className="text-center">
            <div className="text-red-500 text-4xl mb-4">⚠️</div>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={() => navigate('/seller/store')}
              className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600"
            >
              내 가게로 돌아가기
            </button>
          </div>
        </div>
      </SellerPageContainer>
    );
  }

  return (
    <SellerPageContainer activeNavId="store">
      <div className="pb-4">
        {/* 헤더 */}
        <div className="flex items-center mb-6">
          <button
            onClick={() => navigate('/seller/store')}
            className="mr-4 p-2 rounded-lg hover:bg-gray-100"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-xl font-bold text-gray-800">판매내역</h1>
        </div>

        {/* 매출 기간 선택 */}
        <div className="bg-white rounded-lg p-4 mb-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-800">매출 기간</h2>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              {monthOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* 매출 현황 */}
          {salesMetrics && (
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-green-50 rounded-lg p-4">
                <h3 className="text-sm text-green-600 font-medium">월 매출</h3>
                <p className="text-2xl font-bold text-green-700">
                  {formatCurrency(salesMetrics.monthlyRevenue)}원
                </p>
              </div>
              <div className="bg-blue-50 rounded-lg p-4">
                <h3 className="text-sm text-blue-600 font-medium">판매 건수</h3>
                <p className="text-2xl font-bold text-blue-700">
                  {salesMetrics.monthlySalesCount}건
                </p>
              </div>
            </div>
          )}
        </div>

        {/* 판매 내역 목록 */}
        <div className="space-y-4">
          {allOrders.length === 0 ? (
            <div className="bg-white rounded-lg p-8 text-center shadow-sm">
              <p className="text-gray-500">선택한 월에 주문 내역이 없습니다.</p>
            </div>
          ) : (
            allOrders.map((order) => (
              <div key={order.id} className="bg-white rounded-lg p-4 shadow-sm">
                {/* 주문 유형 및 상태 배지 */}
                <div className="flex justify-between items-start mb-3">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${getOrderTypeBadgeColor(order.order_type)}`}>
                    {getOrderTypeLabel(order.order_type)}
                  </span>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${getOrderStatusBadgeColor(order.status)}`}>
                    {getOrderStatusLabel(order.status)}
                  </span>
                </div>

                {/* 주문 정보 */}
                <div className="space-y-2 mb-4">
                  <div className="flex justify-between">
                    <span className="text-gray-600 text-sm">주문자</span>
                    <span className="font-medium">{order.buyer_nickname}</span>
                  </div>
                  
                  <div className="flex justify-between">
                    <span className="text-gray-600 text-sm">주문일시</span>
                    <span className="font-medium text-sm">{formatDateTime(getEffectiveOrderTime(order) || order.created_at)}</span>
                  </div>
                  
                  <div className="flex justify-between">
                    <span className="text-gray-600 text-sm">주문번호</span>
                    <span className="font-medium text-gray-800">{order.order_number}</span>
                  </div>
                  
                  <div className="flex justify-between">
                    <span className="text-gray-600 text-sm">주문품목</span>
                    <span className="font-medium text-right text-sm max-w-[180px] truncate">
                      {getItemsText(order.items)}
                    </span>
                  </div>
                  
                  <div className="flex justify-between">
                    <span className="text-gray-600 text-sm">금액</span>
                    <span className={`font-bold ${
                      order.status === 'COMPLETED' 
                        ? 'text-green-600' 
                        : order.status === 'CANCELLED' || order.status === 'NOSHOW'
                          ? 'text-red-500'
                          : 'text-gray-600'
                    }`}>
                      {formatCurrency(order.total_amount)}원
                    </span>
                  </div>
                </div>

                {/* 액션 버튼들 */}
                <div className="flex space-x-2">
                  <button
                    onClick={() => navigate(`/seller/orders/${order.id}`, {
                      state: { returnTo: '/seller/store/sales-history' }
                    })}
                    className="flex-1 py-2 px-4 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm"
                  >
                    주문상세
                  </button>
                  <button
                    onClick={handleReviewClick}
                    className="flex-1 py-2 px-4 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm"
                  >
                    리뷰보기
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <ToastContainer />
    </SellerPageContainer>
  );
}
